/**
 * @module HypothesisExplanationBlock
 * @purpose Renders the deterministic explanation statement with basis accounting chips and neutrality indicator.
 * @depends types/reasoning (Explanation), react-i18next
 * @dependedOnBy HypothesisCard
 *
 * @rules
 * - Performance: pure display — props only, no state
 * - State: none
 * - Errors: n/a — purely presentational
 */

import { useTranslation } from 'react-i18next'
import type { Explanation } from '../../../../../types/reasoning'

interface HypothesisExplanationBlockProps {
  explanation: Explanation
}

const CONTAINER_STYLE: React.CSSProperties = {
  borderTop: '1px solid var(--border)',
  padding: '10px 0',
}

const SECTION_HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  marginBottom: '6px',
}

const STATEMENT_BLOCK: React.CSSProperties = {
  borderLeft: '3px solid var(--accent)',
  paddingLeft: '10px',
  paddingTop: '4px',
  paddingBottom: '4px',
  fontFamily: 'var(--font-body)',
  fontSize: '0.82rem',
  color: 'var(--text)',
  marginBottom: '8px',
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  marginRight: '4px',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  background: 'transparent',
}

const ACCENT_CHIP: React.CSSProperties = {
  ...CHIP_BASE,
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
}

/**
 * Renders the bounded explanation statement with basis chips.
 *
 * @behaviour Shows statement in a left-accent block, then basis chips:
 *   distinct matches, corroborating artefact types, required-observable flag, absences surfaced.
 *   Tool-category chips render below when basis.categories is present and non-empty (absent on
 *   rows persisted before this feature, or empty for non-reference predicates — ADV-2/ADV-3).
 *   Neutrality indicator shown below chips.
 * @errors n/a
 */
export function HypothesisExplanationBlock({ explanation }: HypothesisExplanationBlockProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const { basis, statement, neutralityChecked } = explanation
  // ADV-2: categories is absent on rows persisted before this feature — default to [].
  const categories = basis.categories ?? []

  return (
    <div style={CONTAINER_STYLE}>
      <div style={SECTION_HEADING}>{t('reasoning.hypothesis.explanation.title')}</div>
      <div style={STATEMENT_BLOCK}>{statement}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        <span style={CHIP_BASE}>
          {t('reasoning.hypothesis.explanation.distinctMatches', { count: basis.distinctMatches })}
        </span>
        <span style={CHIP_BASE}>
          {t('reasoning.hypothesis.explanation.corroboratingTypes', { count: basis.corroboratingArtifactTypes.length })}
        </span>
        {basis.requiredDeclared && basis.requiredMatched && (
          <span style={ACCENT_CHIP}>{t('reasoning.hypothesis.explanation.requiredMatched')}</span>
        )}
        <span style={CHIP_BASE}>
          {t('reasoning.hypothesis.explanation.absencesSurfaced', { count: basis.absencesSurfaced })}
        </span>
      </div>
      {categories.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px', alignItems: 'center' }}>
          <span style={{ ...CHIP_BASE, border: 'none', padding: '1px 0', marginRight: '2px' }}>
            {t('reasoning.hypothesis.explanation.categoriesLabel')}:
          </span>
          {categories.map((category) => (
            <span key={category} style={CHIP_BASE}>{category}</span>
          ))}
        </div>
      )}
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '0.6rem',
        marginTop: '4px',
        color: neutralityChecked ? 'var(--text-dim)' : 'var(--text-muted)',
      }}>
        {neutralityChecked
          ? t('reasoning.hypothesis.explanation.neutralityChecked')
          : t('reasoning.hypothesis.explanation.neutralityNotChecked')}
      </div>
    </div>
  )
}
